// src/features/AudioRecorder.jsx
import React, { useState, useRef, useEffect } from 'react';
import { LiveAudioVisualizer } from 'react-audio-visualize';
import { MicrophoneIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Tooltip from '../components/SharedComponents/ToolTip';
import axios from 'axios';

const EndPoint = process.env.REACT_APP_API_ENDPOINT;

const pickMime = () => {
  if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) return 'audio/webm;codecs=opus';
  if (MediaRecorder.isTypeSupported('audio/webm')) return 'audio/webm';
  if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) return 'audio/ogg;codecs=opus';
  if (MediaRecorder.isTypeSupported('audio/mp4;codecs=mp4a.40.2')) return 'audio/mp4;codecs=mp4a.40.2';
  if (MediaRecorder.isTypeSupported('audio/aac')) return 'audio/aac';
  return '';
};

function extForMime(mime) {
  if (!mime) return 'webm';
  if (mime.includes('ogg')) return 'ogg';
  if (mime.includes('webm')) return 'webm';
  if (mime.includes('mp4')) return 'm4a';
  if (mime.includes('aac')) return 'aac';
  if (mime.includes('mpeg') || mime.includes('mp3')) return 'mp3';
  if (mime.includes('wav')) return 'wav';
  return 'webm';
}

function formatTime(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

async function getAudioDuration(file) {
  return new Promise((resolve, reject) => {
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => { URL.revokeObjectURL(audio.src); resolve(audio.duration); };
    audio.onerror = (e) => reject(e);
    audio.src = URL.createObjectURL(file);
  });
}

const AudioRecorder = ({ onInputUpdate, compact = false }) => {
  const [recording, setRecording]       = useState(false);
  const [mediaStream, setMediaStream]   = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [showTooltip, setShowTooltip]   = useState(false);

  // ── Compact popup state ────────────────────────────────────────────────────
  const [showPopup, setShowPopup]       = useState(false);
  const [duration, setDuration]         = useState(0);
  // 'idle' | 'uploading' | 'done' | 'error'
  const [uploadStatus, setUploadStatus] = useState('idle');

  const timerRef        = useRef(null);
  const fullTranscriptRef = useRef('');
  const toastIdRef      = useRef(null);
  const uploadingRef    = useRef(false);
  const abortCtrlRef    = useRef(null);
  const lastChunkTimeRef = useRef(0);

  // ── Timer driven by recording state ────────────────────────────────────────
  useEffect(() => {
    if (recording) {
      setDuration(0);
      timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
    } else {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [recording]);

  const toggleRecording = () => (recording ? stopRecording() : startRecording());

  const startRecording = async () => {
    // Open popup immediately when compact
    if (compact) { setShowPopup(true); setUploadStatus('idle'); }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMediaStream(stream);

      const selectedMime = pickMime();
      const mr = new MediaRecorder(stream, { mimeType: selectedMime });
      const chunks = [];
      let recordedMime = '';

      mr.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunks.push(event.data);
          recordedMime = recordedMime || event.data.type || selectedMime || '';
          lastChunkTimeRef.current = Date.now();
        }
      };

      mr.onstop = async () => {
        if (compact) setUploadStatus('uploading');

        try {
          const blob = new Blob(chunks, { type: recordedMime || undefined });
          const ext = extForMime(recordedMime || selectedMime);
          const file = new File([blob], `recording-${Date.now()}.${ext}`, {
            type: recordedMime || selectedMime || undefined,
          });

          if (file.size < 1024) throw new Error('Empty/too-small recording (size < 1KB).');

          const fd = new FormData();
          fd.append('file', file, file.name);
          fd.append('mime', file.type || '');

          const resp = await axios.post(`${EndPoint}/careplan/transcribe`, fd, {
            withCredentials: true,
            responseType: 'json',
          });

          onInputUpdate?.(resp.data);

          if (compact) {
            setUploadStatus('done');
            setTimeout(() => setShowPopup(false), 1200);
          }

          toast.update(toastIdRef.current, {
            render: 'Recording uploaded successfully',
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 1500,
          });
          toastIdRef.current = null;
        } catch (err) {
          console.error('Upload error:', err);
          if (compact) setUploadStatus('error');
          toast.update(toastIdRef.current, {
            render: `Upload failed${err.response?.status ? ` (${err.response.status})` : ''}`,
            type: toast.TYPE.ERROR,
            isLoading: false,
            autoClose: 2000,
          });
        }
      };

      mr.start();
      setMediaRecorder(mr);
      setRecording(true);
    } catch (err) {
      console.error(err);
      toast.error('Microphone access failed.');
      if (compact) setUploadStatus('error');
      stopRecording(true);
    }
  };

  const stopRecording = (fromError = false) => {
    setRecording(false);

    try {
      if (mediaRecorder && mediaRecorder.state === 'recording') {
        if (Date.now() - lastChunkTimeRef.current > 500) mediaRecorder.requestData();
        mediaRecorder.stop();
      }
    } catch (_) {}

    if (abortCtrlRef.current) {
      try { abortCtrlRef.current.abort(); } catch (_) {}
      abortCtrlRef.current = null;
    }

    if (mediaStream) {
      mediaStream.getTracks().forEach((t) => t.stop());
      setMediaStream(null);
    }

    if (!fromError && toastIdRef.current) {
      toast.update(toastIdRef.current, {
        render: 'Recording stopped',
        type: toast.TYPE.SUCCESS,
        isLoading: false,
        autoClose: 1100,
      });
      toastIdRef.current = null;
    }

    setMediaRecorder(null);
  };

  const deidentifyText = (input) =>
    (input || '')
      .replace(/\b[A-Z][a-z]+ [A-Z][a-z]+\b/g, '[Name]')
      .replace(/\b\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\b/g, '[Date]')
      .replace(/\b\d{4,5}\s?[A-Za-z]+\s[A-Za-z]+\b/g, '[Address]')
      .replace(/\b\d{10,12}\b/g, '[Phone/ID]')
      .replace(/\b(?:Mr|Mrs|Ms|Dr)\.?\s[A-Z][a-z]+\b/g, '[Title Name]');

  // ── Compact / toolbar mode ────────────────────────────────────────────────
  if (compact) {
    const statusLabel =
      uploadStatus === 'uploading' ? 'Uploading…' :
      uploadStatus === 'done'      ? 'Done ✓' :
      uploadStatus === 'error'     ? 'Upload failed' :
      recording                    ? 'REC' : 'Stopped';

    const statusBg =
      uploadStatus === 'uploading' ? 'rgba(251,191,36,0.25)' :
      uploadStatus === 'done'      ? 'rgba(34,197,94,0.22)'  :
      uploadStatus === 'error'     ? 'rgba(239,68,68,0.22)'  :
      recording                    ? 'rgba(239,68,68,0.22)'  : 'rgba(255,255,255,0.12)';

    const statusColor =
      uploadStatus === 'uploading' ? '#fcd34d' :
      uploadStatus === 'done'      ? '#86efac'  :
      uploadStatus === 'error'     ? '#fca5a5'  :
      recording                    ? '#fca5a5'  : 'rgba(255,255,255,0.65)';

    return (
      <>
        {/* ── Toolbar button ── */}
        <button
          type="button"
          onClick={toggleRecording}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border shadow-sm text-sm font-medium transition-all ${
            recording
              ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
              : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
          }`}
        >
          <MicrophoneIcon className={`h-4 w-4 ${recording ? 'text-red-500' : 'text-slate-500'}`} />
          <span>{recording ? 'Stop' : 'Voice'}</span>
          {recording && (
            <span className="w-2 h-2 rounded-full bg-red-500 animate-ping flex-shrink-0" />
          )}
        </button>

        {/* ── Recording popup ── */}
        {showPopup && (
          <div style={{
            position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
            width: 340, borderRadius: 20, overflow: 'hidden',
            border: '1.5px solid #bae6fd', background: '#fff',
            boxShadow: '0 8px 40px rgba(12,74,110,0.20)',
            filter: 'drop-shadow(0 4px 24px rgba(12,74,110,0.14))',
          }}>

            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #0c4a6e 0%, #0369a1 60%, #0e7490 100%)',
              padding: '12px 16px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {/* Pulse dot */}
                <div style={{ position: 'relative', width: 10, height: 10, flexShrink: 0 }}>
                  <div style={{
                    position: 'absolute', inset: 0, borderRadius: '50%',
                    background: recording ? '#f87171' : (uploadStatus === 'uploading' ? '#fcd34d' : '#94a3b8'),
                    animation: recording ? 'arPulse 1.2s ease-in-out infinite' : 'none',
                  }} />
                  {recording && (
                    <div style={{
                      position: 'absolute', inset: -3, borderRadius: '50%',
                      border: '2px solid rgba(248,113,113,0.45)',
                      animation: 'arRing 1.2s ease-in-out infinite',
                    }} />
                  )}
                </div>
                <span style={{ color: '#fff', fontWeight: 700, fontSize: 13, letterSpacing: '0.01em' }}>
                  Voice Recording
                </span>
                <span style={{
                  background: statusBg, color: statusColor,
                  borderRadius: 100, padding: '2px 10px',
                  fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                }}>
                  {statusLabel}
                </span>
              </div>

              <button
                onClick={() => setShowPopup(false)}
                style={{
                  background: 'rgba(255,255,255,0.12)', border: 'none',
                  borderRadius: 8, padding: '4px 6px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', color: 'rgba(255,255,255,0.75)',
                }}
                title="Close"
              >
                <XMarkIcon style={{ width: 15, height: 15 }} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '14px 16px 16px', background: '#f0f9ff' }}>

              {/* Visualizer */}
              <div style={{
                background: '#fff', borderRadius: 10,
                border: '1px solid #e0f2fe', padding: '6px 10px',
                display: 'flex', alignItems: 'center', height: 52, marginBottom: 12,
              }}>
                {recording && mediaRecorder ? (
                  <LiveAudioVisualizer
                    mediaRecorder={mediaRecorder}
                    width={284} height={38}
                    barWidth={2} gap={1.5}
                    barColor="#0369a1"
                  />
                ) : (
                  <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
                    {uploadStatus === 'uploading' && 'Processing your recording…'}
                    {uploadStatus === 'done'      && 'Done — opening Smart Paste…'}
                    {uploadStatus === 'error'     && 'Upload failed, please try again.'}
                    {uploadStatus === 'idle' && !recording && 'Waiting for audio…'}
                  </span>
                )}
              </div>

              {/* Timer */}
              {recording && (
                <div style={{
                  textAlign: 'center', color: '#0369a1',
                  fontWeight: 800, fontSize: 22, fontVariantNumeric: 'tabular-nums',
                  letterSpacing: '0.05em', marginBottom: 12,
                }}>
                  {formatTime(duration)}
                </div>
              )}

              {/* Stop button — shown while recording */}
              {recording && (
                <button
                  onClick={toggleRecording}
                  style={{
                    width: '100%',
                    background: 'linear-gradient(135deg, #dc2626, #ef4444)',
                    border: 'none', borderRadius: 12, padding: '11px',
                    color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    boxShadow: '0 2px 12px rgba(220,38,38,0.35)',
                    transition: 'opacity 0.15s',
                  }}
                >
                  <MicrophoneIcon style={{ width: 16, height: 16 }} />
                  Stop Recording
                </button>
              )}

              {/* Uploading spinner */}
              {uploadStatus === 'uploading' && !recording && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: 8, padding: '8px 0', color: '#0369a1', fontSize: 13, fontWeight: 600,
                }}>
                  <div style={{
                    width: 16, height: 16, border: '2px solid #bae6fd',
                    borderTopColor: '#0369a1', borderRadius: '50%',
                    animation: 'arSpin 0.7s linear infinite',
                  }} />
                  Uploading…
                </div>
              )}
            </div>

            {/* Keyframes */}
            <style>{`
              @keyframes arPulse {
                0%, 100% { opacity: 1; transform: scale(1); }
                50%       { opacity: 0.6; transform: scale(0.85); }
              }
              @keyframes arRing {
                0%   { opacity: 0.8; transform: scale(0.9); }
                100% { opacity: 0;   transform: scale(1.8); }
              }
              @keyframes arSpin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        )}
      </>
    );
  }

  // ── Full / standalone mode ────────────────────────────────────────────────
  return (
    <div className="flex flex-col items-center my-4 h-auto">
      <div className="flex items-center space-x-4 sm:ml-[700px]">
        <button
          id="microphone"
          className={`${
            recording ? 'bg-red-600 hover:bg-red-700' : 'bg-sky-800 hover:bg-sky-900'
          } flex items-center text-white px-2 py-2 rounded-3xl hover:shadow-xl relative`}
          onClick={toggleRecording}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <MicrophoneIcon className="w-8 h-8" />
          {recording && <p className="px-2">Recording</p>}
        </button>

        {showTooltip && (
          <div className="absolute ml-80 -mt-20">
            <Tooltip text="Voice recording" />
          </div>
        )}

        <div style={{ width: 300, height: 50 }}>
          {recording && mediaRecorder ? (
            <LiveAudioVisualizer
              mediaRecorder={mediaRecorder}
              width={300}
              height={50}
              barWidth={3}
              gap={2}
              barColor={'#205680'}
            />
          ) : null}
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default AudioRecorder;
